import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getImageUrl } from '../utils/imageUtils';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { apiWithAuth } from '../api';

const VendorDirectory = () => {
  const theme = useTheme();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 12;

  // Fetch all vendors
  const { data: vendors, isLoading, error } = useQuery(
    ['vendors', page, searchQuery, categoryFilter],
    async () => {
      const token = localStorage.getItem('token');
      const response = await apiWithAuth(token).get('/vendors/', {
        params: {
          skip: (page - 1) * itemsPerPage,
          limit: itemsPerPage,
          search: searchQuery,
          category: categoryFilter,
        }
      });
      return response.data;
    },
    {
      retry: 1,
    }
  );

  // Fetch categories for filter
  const { data: categories } = useQuery(
    ['categories'],
    async () => {
      const token = localStorage.getItem('token');
      const response = await apiWithAuth(token).get('/categories/');
      return response.data;
    }
  );

  const handleVendorClick = (vendorId) => {
    history.push(`/vendors/${vendorId}`);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handleCategoryChange = (event) => {
    setCategoryFilter(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading vendors...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Error loading vendors. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
          Vendor Directory
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          Discover amazing vendors and their services
        </Typography>
      </Box>

      {/* Search and Filter Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={handleCategoryChange}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories?.map((category) => (
                  <MenuItem key={category.category_id} value={category.category_id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary">
              {vendors?.length || 0} vendors found
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Vendors Grid */}
      {vendors?.length > 0 ? (
        <>
          <Grid container spacing={3}>
            {vendors.map((vendor) => (
              <Grid item xs={12} sm={6} md={4} key={vendor.vendor_id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                    }
                  }}
                  onClick={() => handleVendorClick(vendor.vendor_id)}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={vendor.cover_photo_url ? getImageUrl(vendor.cover_photo_url) : 'https://via.placeholder.com/400x200?text=Vendor+Cover'}
                    alt={vendor.business_name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        src={vendor.profile_picture_url ? getImageUrl(vendor.profile_picture_url) : undefined}
                        sx={{ width: 60, height: 60, mr: 2 }}
                      >
                        {!vendor.profile_picture_url && (
                          <PersonIcon sx={{ fontSize: 30 }} />
                        )}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                          {vendor.business_name}
                        </Typography>
                        {vendor.category_name && (
                          <Chip
                            icon={<CategoryIcon />}
                            label={vendor.category_name}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        )}
                      </Box>
                    </Box>
                    
                    {vendor.bio && (
                      <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                        {vendor.bio.length > 100 
                          ? `${vendor.bio.substring(0, 100)}...` 
                          : vendor.bio
                        }
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {vendor.contact_info && (
                        <Chip
                          icon={<LocationIcon />}
                          label={vendor.contact_info}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StarIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                          {vendor.average_rating?.toFixed(1) || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                          {vendor.total_applications || 0} events
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                      fullWidth 
                      variant="contained" 
                      onClick={() => handleVendorClick(vendor.vendor_id)}
                    >
                      View Profile
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {vendors.length === itemsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={Math.ceil((vendors.length || 0) / itemsPerPage)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No vendors found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Try adjusting your search criteria or check back later.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default VendorDirectory;

